/**
 * Whatever is open over the builder: the ⋯ menu, the search, and the one
 * popover the adapter says is being edited (moved out of
 * `pages/quote-builder-v2.tsx` at step 8 — the markup is unchanged).
 */
import { useEffect } from 'react'
import { can, type MeResponse } from '@rowted/shared'
import { ContextMenu } from '../components/context-menu.js'
import { VisitMarginPopover } from '../builder/margin/visit-margin.js'
import { LinePopover } from '../builder/popovers/line-popover.js'
import { ServicePopover } from '../builder/popovers/service-popover.js'
import { MeasurePopover } from '../builder/popovers/measure-popover.js'
import { request } from '../lib/api.js'
import { VisitPopover } from '../builder/popovers/visit-popover.js'
import { findLine, findService, type QuoteBuilder } from '../builder/adapters/quote.js'
import { ProposalVisitPopover } from './proposal-visit-popover.js'
import { AddSearch, PackageEditor } from './quote-editors.js'
import { AddVisitMenu } from './add-visit-menu.js'

export function QuoteEditorLayer({ qb, me }: { qb: QuoteBuilder; me: MeResponse }) {
  const {
    quote, error, menu, setMenu, editing, setEditing, setMarginScope,
    setSummaryOpen, anchor, openService, divisions, mutate, createChoice, railHidden,
    addPackage, addService, add, startVisit, duplicateVisit, openTab, addServiceVisit,
  } = qb
  if (!quote) return null
  return (
    <>
    {menu && (
      <ContextMenu items={menu.items} x={menu.x} y={menu.y} onClose={() => setMenu(null)} />
    )}

    <AddVisitMenu
      open={editing?.kind === 'addVisit'}
      anchor={anchor.current}
      onClose={() => setEditing(null)}
      visits={quote.visits}
      onStart={startVisit}
      onCopy={(visitId) => void duplicateVisit(visitId)}
    />

    {editing?.kind === 'add' && (
      <AddSearch
        open
        quoteId={quote.id}
        propertyId={quote.propertyId}
        anchor={anchor.current}
        startKind={editing.startKind}
        hasOpenService={openService !== null}
        onClose={() => setEditing(null)}
        onPick={(pick, asNewService) => void add(pick, asNewService, editing.afterLineId)}
      />
    )}

    {editing?.kind === 'margin' && (() => {
      const visit = quote.visits.find((one) => one.id === editing.visitId)
      if (!visit) return null
      return (
        <VisitMarginPopover
          key={visit.id}
          visit={visit}
          anchor={anchor.current}
          onClose={() => setEditing(null)}
          targetPct={quote.totals.targetMarginPct}
          wholeQuotePct={quote.totals.margin.scopes.base.marginPct}
          onOpenMargin={() => {
            setEditing(null)
            setMarginScope(visit.margin.base ? 'base' : 'options')
            if (railHidden) setSummaryOpen(true)
            else document.getElementById('margin-card')?.scrollIntoView({ block: 'start', behavior: 'smooth' })
          }}
        />
      )
    })()}

    {editing?.kind === 'proposal' && (() => {
      const visit = quote.visits.find((one) => one.id === editing.visitId)
      if (!visit) return null
      return (
        <ProposalVisitPopover
          open
          anchor={anchor.current}
          visit={visit}
          /*
           * The number the *customer's* card shows, which counts the
           * included visits and nothing else — so "visit 2" in this title
           * and "2" on the page are the same visit.
           */
          index={quote.visits.filter((one) => !one.optional && !one.chooseGroupId)
            .findIndex((one) => one.id === visit.id) + 1}
          current={{
            timing: visit.proposal.timing,
            priceView: visit.proposal.priceView,
            detail: visit.proposal.detail,
            phraseId: visit.proposal.phraseId,
            phraseText: visit.proposal.phraseText,
          }}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await mutate(`/visits/${visit.id}`, { method: 'PATCH', body: patch })
          }}
        />
      )
    })()}

    {editing?.kind === 'addPackage' && (
      <AddSearch
        open
        mode="package"
        quoteId={quote.id}
        propertyId={quote.propertyId}
        anchor={anchor.current}
        hasOpenService={false}
        onClose={() => setEditing(null)}
        onPick={(pick) => void addPackage(pick)}
      />
    )}

    {editing?.kind === 'addService' && (
      <AddSearch
        open
        mode="service"
        quoteId={quote.id}
        propertyId={quote.propertyId}
        anchor={anchor.current}
        hasOpenService={false}
        onClose={() => setEditing(null)}
        onPick={(pick) => void addService(editing.visitId, pick, editing.afterServiceId)}
      />
    )}

    {/* Add visit ▾ → From a service…: the same picker, onto a new visit (item 3.2). */}
    {editing?.kind === 'addServiceVisit' && (
      <AddSearch
        open
        mode="service"
        asNewVisit
        quoteId={quote.id}
        propertyId={quote.propertyId}
        anchor={anchor.current}
        hasOpenService={false}
        onClose={() => setEditing(null)}
        onPick={(pick) => void addServiceVisit(pick)}
      />
    )}

    {editing?.kind === 'line' && (() => {
      const found = findLine(quote, editing.lineId)
      if (!found) return null
      return (
        <LinePopover
          line={found.line}
          service={found.service}
          anchor={anchor.current}
          onClose={() => setEditing(null)}
          onSave={(patch) => mutate(`/lines/${found.line.id}`, { method: 'PATCH', body: patch })}
          onReset={() => void mutate(`/lines/${found.line.id}/reset`, { method: 'POST' })}
          onRemove={() => void mutate(`/lines/${found.line.id}`, { method: 'DELETE' })}
          problem={error}
        />
      )
    })()}

    {editing?.kind === 'visit' && (() => {
      const visit = quote.visits.find((candidate) => candidate.id === editing.visitId)
      if (!visit) return null
      return (
        <VisitPopover
          visit={visit}
          plans={quote.plans}
          options={quote.chooseOptions}
          groups={quote.chooseGroups}
          onCreateChoice={createChoice}
          onRenameQuestion={(groupId, name) => mutate(
            `/choose-groups/${groupId}`, { method: 'PATCH', body: { name } })}
          people={[]}
          anchor={anchor.current}
          section={editing.section}
          onClose={() => setEditing(null)}
          onOpenBilling={() => { setEditing(null); openTab('billing') }}
          onSave={(patch) => mutate(`/visits/${visit.id}`, { method: 'PATCH', body: patch })}
          quote={quote}
          onSaveWhen={(when, followers) => mutate(`/visits/${visit.id}/when`,
            { method: 'PUT', body: { ...when, followers } })}
          onRemove={() => void mutate(`/visits/${visit.id}`, { method: 'DELETE' })}
          /*
           * One request: the plan route takes a `visitId` and does both, so
           * a plan can never be made and then left empty because the second
           * call failed.
           */
          onCreatePlan={async (name) => {
            await mutate('/plans', {
              method: 'POST',
              body: { name: name || undefined, visitId: visit.id },
            })
          }}
        />
      )
    })()}

    {editing?.kind === 'service' && (() => {
      const found = findService(quote, editing.serviceId)
      if (!found) return null
      return (
        <ServicePopover
          service={found}
          divisions={divisions}
          quoteId={quote.id}
          propertyId={quote.propertyId}
          quoteReference={quote.reference}
          canMeasure={can(me.permissions, 'properties', 'edit')}
          anchor={anchor.current}
          onClose={() => setEditing(null)}
          onSave={(patch) => mutate(`/services/${found.id}`, { method: 'PATCH', body: patch })}
          onRemove={() => void mutate(`/services/${found.id}`, { method: 'DELETE' })}
          onResetMeasure={() => mutate(
            `/services/${found.id}/measure/reset`, { method: 'POST' })}
          onClearMeasure={() => mutate(
            `/services/${found.id}`, { method: 'PATCH', body: { measure: null } })}
        />
      )
    })()}
    {/* Rename on a service's ⋮ (item 4): the popover above, opened on its Name. */}
    {editing?.kind === 'service' && editing.focus === 'name' && (
      <FocusOnOpen key={editing.serviceId} label="Service name" />
    )}

    {(editing?.kind === 'measure' || editing?.kind === 'packageMeasure') && (() => {
      /*
       * One popover off either chip — a service's or a package group's
       * (Aaron, review item 4.1). What differs is said here rather than asked
       * inside it: where Done writes, the tile, and whether the server has a
       * projection for the strip (a package has none).
       */
      const service = editing.kind === 'measure' ? findService(quote, editing.serviceId) : null
      const instance = editing.kind === 'packageMeasure'
        ? quote.packages.find((one) => one.id === editing.instanceId) ?? null
        : null
      const found = service ?? instance
      if (!found) return null
      const path = service ? `/services/${service.id}` : `/packages/${found.id}`
      return (
        <MeasurePopover
          key={found.id}
          subject={found}
          tile={service ? 'service' : 'package'}
          projectionPath={service ? `/quotes/${quote.id}/services/${service.id}/projection` : null}
          propertyId={quote.propertyId}
          place={quote.propertyName}
          canMeasure={can(me.permissions, 'properties', 'edit')}
          anchor={anchor.current}
          onClose={() => setEditing(null)}
          onDone={async (choice) => {
            /*
             * B with *Save it on the property too*: one row through the
             * property's own writer, then the service or package is linked
             * to it — so the quote follows the property from here on (§6.7
             * item 4). Null is *A number for this quote only*, and unlinks.
             */
            let measurementId = choice.measurementId
            if (choice.saveToProperty && quote.propertyId && found.measurementTypeId) {
              const saved = await request<{ id: string }>(
                `/measurements/${quote.propertyId}/${found.measurementTypeId}`,
                { method: 'PUT', body: { value: choice.measure, source: 'typed', measuredOn: new Date().toISOString().slice(0, 10) } })
              measurementId = saved.id
            }
            return mutate(path, {
              method: 'PATCH', body: { measure: choice.measure, measurementId },
            })
          }}
        />
      )
    })()}

    {editing?.kind === 'package' && (() => {
      const found = quote.packages.find((one) => one.id === editing.instanceId)
      if (!found) return null
      return (
        <PackageEditor
          instance={found}
          anchor={anchor.current}
          onClose={() => setEditing(null)}
          onSave={(patch) => mutate(
            `/packages/${found.id}`, { method: 'PATCH', body: patch })}
          onDetach={() => void mutate(
            `/packages/${found.id}/detach`, { method: 'POST' })}
          onResetMeasure={() => mutate(
            `/packages/${found.id}/measure/reset`, { method: 'POST' })}
          onClearMeasure={() => mutate(
            `/packages/${found.id}`, { method: 'PATCH', body: { measure: null } })}
        />
      )
    })()}
    </>
  )
}

/**
 * The caret in one field of the popover that has just opened, found by its
 * accessible name, with what is in it selected — a service ⋮'s **Rename**
 * (Aaron, review item 4), the visit's `section: 'name'` one level down.
 *
 * A sibling rather than a prop, so the popover's own `autoFocus` stays where
 * it is for every other way in (a measured service opens on its Measure).
 * An effect runs after the commit that mounted both, so the name wins.
 */
function FocusOnOpen({ label }: { label: string }) {
  useEffect(() => {
    const box = document.querySelector<HTMLInputElement>(`input[aria-label="${label}"]`)
    box?.focus()
    box?.select()
  }, [label])
  return null
}
